import React, { useRef, useState, useMemo, useEffect } from "react";
import ReactDOM from "react-dom/client";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";

import { SketchMark } from "pages/de/styles/detail/components/DevelopStage/Sketch";
import ReactPlayer from "react-player";
import { pdfjs, Document, Page } from "react-pdf/dist/esm/entry.webpack5";
import "react-pdf/dist/esm/Page/TextLayer.css";

import { Circle, Image as KonvaImage, Label, Layer, Line, Shape, Stage, Text } from "react-konva";
import useImage from "use-image";
import CommentsImg_Azure from "assets/images/comments-Azure.svg";
import CommentsImg_Bisque from "assets/images/comments-Bisque.svg";
import CommentsImg_Black from "assets/images/comments-Black.svg";
import CommentsImg_Cyan from "assets/images/comments-Cyan.svg";
import CommentsImg_MediumSpringGreen from "assets/images/comments-MediumSpringGreen.svg";
import CommentsImg_Tomato from "assets/images/comments-Tomato.svg";
import CommentsImg_Violet from "assets/images/comments-Violet.svg";

import PDFIcon from "assets/images/upstyled/pdf_icon.jpeg";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

import defaultStyleImage from "assets/images/upstyled/default_background.png";
import { emptyImage, noImageaAvailable } from "utils/data";

import { canResizeImageUrl, getStyleImages, getFileImage, getBase64 } from "utils/imageHelper";
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

/**
 * mode:"normal","tech-pack","reference"
 * 1.images from design/others directly uploaded to server.
 * 2.images from tech pack which includes measurement info and comment info
 * 3.images from references which includes comment info
 * File Type: image/pdf/video(TODO)
 */

const ImageCardWrapper = ({
  data,
  mode = "normal",
  width,
  height,
  defaultImage = defaultStyleImage,
  isOriginalSize = false,
  resizeType = "m_fill", //m_mfit
  resizeLimit = null,
  iconMode = false,
  pdfPagination = false,
  videoProps = {},
  ...rest
}) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [imgUri, setImgUri] = useState(null);

  const sketchRef = useRef(null);
  const tempRef = useRef(null);
  const parentRef = rest?.parentRef || null;

  const random = new Date().valueOf();

  let isWidthNumber = !isNaN(Number(width));
  let isHeightNumber = !isNaN(Number(height));
  if (width && typeof width == "string" && width.endsWith("%")) {
    isWidthNumber = false;
  }
  if (height && typeof height == "string" && height.endsWith("%")) {
    isHeightNumber = false;
  }

  useEffect(() => {
    if (sketchRef && typeof data == "object" && data?.charid && mode == "dataUri") {
      getImageDataUrl();
    } else if (data?.preview && data?.type == "application/pdf") {
      convertToUri();
    }
  }, [data?.id, tempRef, data?.preview]);

  const convertToUri = async () => {
    const uri = await getBase64(data);
    setImgUri(uri);
  };

  const dataUrl = useMemo(() => {
    return data?.url;
  }, [data]);
  const [background] = useImage(data?.imgurl || "", "anonymous", "origin");
  var commentsImgs = {
    Azure: useImage(CommentsImg_Azure),
    Bisque: useImage(CommentsImg_Bisque),
    Black: useImage(CommentsImg_Black),
    Cyan: useImage(CommentsImg_Cyan),
    MediumSpringGreen: useImage(CommentsImg_MediumSpringGreen),
    Violet: useImage(CommentsImg_Violet),
    Tomato: useImage(CommentsImg_Tomato),
  };

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
  }

  const getImageDataUrl = async () => {
    if (sketchRef) {
      const uri = sketchRef?.current?.toDataURL();
      setImgUri(uri);
    }
  };

  if (data?.preview) {
    if (data?.type == "application/pdf") {
      return (
        <SuiBox
          className={pdfPagination ? "pdf-wrapper" : ""}
          {...rest}
          sx={{
            overflow: "hidden",
            width: width ? width : "auto",
            height: height ? height : "auto",
            objectFit: "contain",
            objectPosition: "center",
            textAlign: "center",
            position: "relative",
            ...rest?.sx,
          }}
        >
          <Document
            file={imgUri}
            className="flex justify-center"
            onLoadSuccess={onDocumentLoadSuccess}
            options={{
              standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
            }}
          >
            <Page
              pageNumber={pageNumber}
              width={width}
              height={height}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              renderForms={false}
              style={{ margin: "0 auto" }}
              {...rest}
            />
          </Document>
          {pdfPagination ? (
            <SuiBox
              className="page-controls"
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ whitespace: "noWrap", flexWrap: "nowrap" }}
            >
              <SuiButton
                disabled={pageNumber == 1}
                variant="text"
                color="dark"
                onClick={(e) => {
                  e.stopPropagation();
                  setPageNumber((props) => props - 1);
                }}
                iconOnly
              >
                <KeyboardArrowLeftIcon fontSize="sm" />
              </SuiButton>
              <span style={{ whiteSpace: "nowrap" }}>
                {pageNumber} of {numPages}
              </span>
              <SuiButton
                variant="text"
                onClick={(e) => {
                  e.stopPropagation();
                  setPageNumber((props) => props + 1);
                }}
                color="dark"
                disabled={pageNumber == numPages}
                iconOnly
              >
                <KeyboardArrowRightIcon fontSize="sm" />
              </SuiButton>
            </SuiBox>
          ) : null}
        </SuiBox>
      );
    }
    return (
      <SuiBox
        src={data?.preview}
        className="flex justify-center"
        component="img"
        {...rest}
        sx={{
          overflow: "hidden",
          width: width ? width : "auto",
          height: height ? height : "auto",
          margin: "0px auto",
          objectFit: "contain",
          objectPosition: "center",
          ...rest?.sx,
        }}
      />
    );
  }

  if (typeof data == "object" && data?.url && !data?.charid) {
    //type=image

    if (
      data?.type?.match("image/*") ||
      (isOriginalSize &&
        !Boolean(data?.type?.match("video")) &&
        !Boolean(data?.type == "application/pdf"))
    ) {
      return (
        <SuiBox
          src={
            isOriginalSize
              ? data?.url + `?random=${random}` || data
              : getFileImage(data, {
                  height: isHeightNumber ? height : null,
                  width: isWidthNumber ? width : null,
                  type: resizeType,
                  resizeLimit,
                })
          }
          className="flex justify-center items-center"
          component="img"
          {...rest}
          sx={{
            overflow: "hidden",
            width: width ? width : "auto",
            height: height ? height : "auto",
            objectFit: "contain",
            objectPosition: "center",
            textAlign: "center",
            position: "relative",
            margin: "0px auto",

            ...rest?.sx,
          }}
        />
      );
    } else if (Boolean(data?.type?.match("video"))) {
      return (
        <SuiBox
          height={height ? height : "100%"}
          width={width ? width : "100%"}
          sx={{ ...rest?.sx }}
          className="flex justify-center items-center"
          {...rest}
        >
          <ReactPlayer
            url={data.url}
            height={height ? height : "100%"}
            width={width ? width : "100%"}
            className="flex justify-center items-center"
            {...videoProps}
          />
        </SuiBox>
      );
    } else if (Boolean(data?.type == "application/pdf")) {
      return (
        <SuiBox
          className={pdfPagination ? "pdf-wrapper" : ""}
          {...rest}
          sx={{
            overflow: "hidden",
            width: width ? width : "auto",
            height: height ? height : "auto",
            objectFit: "contain",
            objectPosition: "center",
            textAlign: "center",
            position: "relative",
            ...rest?.sx,
          }}
        >
          <Document
            file={data.url}
            className="flex justify-center"
            onLoadSuccess={onDocumentLoadSuccess}
            options={{
              standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
            }}
          >
            <Page
              pageNumber={pageNumber}
              width={width}
              height={height}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              renderForms={false}
              style={{ margin: "0 auto" }}
              {...rest}
            />
          </Document>
          {pdfPagination ? (
            <SuiBox
              className="page-controls"
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ whitespace: "noWrap", flexWrap: "nowrap" }}
            >
              <SuiButton
                disabled={pageNumber == 1}
                variant="text"
                color="dark"
                onClick={(e) => {
                  e.stopPropagation();
                  setPageNumber((props) => props - 1);
                }}
                iconOnly
              >
                <KeyboardArrowLeftIcon fontSize="sm" />
              </SuiButton>
              <span style={{ whiteSpace: "nowrap" }}>
                {pageNumber} of {numPages}
              </span>
              <SuiButton
                variant="text"
                onClick={(e) => {
                  e.stopPropagation();
                  setPageNumber((props) => props + 1);
                }}
                color="dark"
                disabled={pageNumber == numPages}
                iconOnly
              >
                <KeyboardArrowRightIcon fontSize="sm" />
              </SuiButton>
            </SuiBox>
          ) : null}
        </SuiBox>
      );
    }
  } else if (typeof data == "object" && data?.charid) {
    const rotation = data.rotation ?? 0;

    data.height = data.height || 1;
    data.width = data.width || 1;
    let m_width = !isNaN(Number(width)) ? width : null || data.width;
    let m_height = !isNaN(Number(height)) ? height : null || data.height;

    if (!isNaN(Number(width))) {
      m_width = width;
      m_height = m_width * (data.height / data.width);
      if (!isNaN(Number(height)) && m_height > height) {
        m_height = height;
        m_width = m_height * (data.width / data.height);
      }
    } else if (!isNaN(Number(height))) {
      m_height = height;
      m_width = m_height * (data.width / data.height);
      if (!isNaN(Number(width)) && m_width > width) {
        m_width = width;
        m_height = m_width * (data.height / data.width);
      }
    }

    return (
      <SuiBox
        ref={tempRef}
        className="flex items-center justify-center"
        {...rest}
        sx={{
          overflow: "hidden",
          width: m_width,
          height: m_height,

          position: "relative",
          marginLeft: "50%",
          WebkitTransform: "translateX(-50%)",
          ...rest?.sx,
        }}
      >
        <div className={mode == "dataUri" ? `absolute -top-[1000px] -left-[1000px]` : ""}>
          <Stage className="konva " width={m_width} height={m_height} ref={sketchRef}>
            <Layer>
              <KonvaImage
                image={background}
                width={rotation == 0 || rotation == 2 ? m_width : m_height}
                height={rotation == 0 || rotation == 2 ? m_height : m_width}
                rotation={rotation * 90}
                offsetX={rotation < 2 ? 0 : rotation == 2 ? m_width : m_height}
                offsetY={rotation == 0 || rotation == 3 ? 0 : rotation == 1 ? m_width : m_height}
              />
              {iconMode
                ? null
                : data.lines?.map((line) => (
                    <SketchMark
                      key={line.id}
                      markdata={line}
                      allowDrawing={{ current: false }}
                      updateFunc={null}
                      actualWidth={m_width}
                      actualHeight={m_height}
                      showEditFunc={null}
                      editable={false}
                      sketchdata={data}
                      setSketchToolTip={() => {}}
                      commentsImgs={commentsImgs}
                      rotation={rotation}
                    />
                  ))}
            </Layer>
          </Stage>
        </div>
        {mode == "dataUri" && imgUri ? (
          <SuiBox
            src={imgUri}
            className="flex justify-center"
            component="img"
            {...rest}
            sx={{
              overflow: "hidden",
              width: width ? width : "auto",
              height: height ? height : "auto",
              margin: "0px auto",
              objectFit: "contain",
              objectPosition: "center",
              ...rest?.sx,
            }}
          />
        ) : null}
      </SuiBox>
    );
  }

  const options = {
    height: isHeightNumber ? height : null,
    width: isWidthNumber ? width : null,
    type: resizeType,
    resizeLimit,
  };
  const emptyImageUrl = isOriginalSize
    ? noImageaAvailable
    : noImageaAvailable +
      `?random=${random}&x-oss-process=image/resize,${options.type}${
        options.height ? `,h_${options.height}` : ""
      }${options.width ? `,w_${options.width}` : ""}${
        options.resizeLimit != null ? `,limit_${options.resizeLimit}` : ""
      }`;

  //return default image
  return (
    // <SuiBox
    //   {...rest}
    //   sx={{
    //     overflow: "hidden",

    //     backgroundImage: ({ functions: { rgba, linearGradient }, palette: { gradients } }) =>
    //       `${linearGradient(
    //         rgba(gradients.secondary.main, 0.2),
    //         rgba(gradients.secondary.state, 0.4)
    //       )}`,
    //     height: height ? height : "100%",
    //     width: width ? width : "100%",
    //     boxShadow: ({ boxShadows: { md } }) => md,
    //     objectFit: "contain",
    //     objectPosition: "center",
    //     textAlign: "center",
    //     margin: "0px auto",

    //     ...rest?.sx,
    //   }}
    // />

    <SuiBox
      src={emptyImageUrl}
      component="img"
      {...rest}
      sx={{
        backgroundImage: ({ functions: { rgba, linearGradient }, palette: { gradients } }) =>
          `${linearGradient(
            rgba(gradients.secondary.main, 0.2),
            rgba(gradients.secondary.state, 0.4)
          )}`,
        height: height ? height : "100%",
        width: width ? width : "100%",
        boxShadow: ({ boxShadows: { md } }) => md,
        objectFit: "contain",
        objectPosition: "center",
        textAlign: "center",
        margin: "0px auto",
        ...rest?.sx,
      }}
    />
  );
};

export default ImageCardWrapper;
